import {
  EnumTagEntityTypes,
  EnumTagGroupKey,
  EnumTagGroupType,
  useTagGroupManyQuery,
  useTagManyQuery,
} from "gql/generated.ts";
import _ from "lodash";
import { getMultilingualString } from "shared/utils";
import { useMemo, useState, useEffect } from "react";

export const useAmenities = () => {
  /* Get all `AMENITY` tag groups */
  const { data: tagGroupData } = useTagGroupManyQuery({
    variables: {
      filter: {
        type: [EnumTagGroupType.Amenity],
      },
    },
  });

  /* Get all `OPERATOR` tags with a `AMENITY` tag group */
  const {
    data: tagData,
    loading: tagsLoading,
    error: tagsError,
  } = useTagManyQuery({
    variables: {
      filter: {
        tagGroup:
          tagGroupData?.tagGroupMany
            ?.filter(
              (tagGroup) =>
                tagGroup.key === EnumTagGroupKey.RoofedAccommodation,
            )
            ?.map((tagGroup) => tagGroup._id) ?? null,
        entityTypes: [EnumTagEntityTypes.Operator],
      },
      limit: 10000,
    },
  });

  /* Tags mapped for the ControlledCombobox component */
  const tagsMappedToCombobox = useMemo(
    () =>
      tagData?.tagMany
        ? _(tagData.tagMany)
            .groupBy("tagGroup.key")
            .map(function (tags, key) {
              return {
                group: key,
                tags: tags.map((tag) => ({
                  value: tag._id,
                  label: getMultilingualString(tag.name),
                })),
              };
            })
            // Sort tags by group and then the tags by label
            .sortBy("group")
            .map((group) => ({
              group: group.group,
              tags: _.sortBy(group.tags, "label"),
            }))
            .value()
        : [],
    [tagData?.tagMany],
  );

  /* Filtered tags mapped to the ControlledCombobox component */
  const [filterableAmenityTags, setFilterableAmenityTags] =
    useState(tagsMappedToCombobox);

  const handleTagInputValueSearch = (search: string) => {
    const inputValue = (search.toLowerCase().split(",").pop() ?? "").trim();
    const filtered = tagsMappedToCombobox
      .map((tagGroup) => {
        return {
          ...tagGroup,
          tags: tagGroup.tags.filter((tag) =>
            tag.label.toLowerCase().includes(inputValue),
          ),
        };
      })
      .filter((tagGroup) => tagGroup.tags.length > 0);
    setFilterableAmenityTags(
      filtered.length > 0 ? filtered : tagsMappedToCombobox,
    );
  };

  const handleComboboxOpenChange = () => {
    setFilterableAmenityTags(tagsMappedToCombobox);
  };

  useEffect(() => {
    setFilterableAmenityTags(tagsMappedToCombobox);
  }, [tagsMappedToCombobox]);

  return {
    tagsLoading,
    tagsError,
    filterableAmenityTags,
    onTagInputValueSearch: handleTagInputValueSearch,
    onComboboxOpenChange: handleComboboxOpenChange,
  };
};
